'use client';

import { useState, useEffect, useCallback, useRef } from 'react';

let showToastFn: ((msg: string) => void) | null = null;
let startProgressFn: ((msg: string) => void) | null = null;
let endProgressFn: ((msg: string, success: boolean) => void) | null = null;

/** Call from anywhere to show a brief save confirmation */
export function flashSaved(msg = 'Changes saved') {
  showToastFn?.(msg);
}

/** Show a persistent toast with a fake progress bar */
export function startSaveProgress(msg = 'Saving deck...') {
  startProgressFn?.(msg);
}

/** Complete or fail the progress toast, then auto-dismiss */
export function endSaveProgress(msg: string, success = true) {
  endProgressFn?.(msg, success);
}

export const SaveToast = () => {
  const [message, setMessage] = useState<string | null>(null);
  const [visible, setVisible] = useState(false);

  // Progress mode state
  const [progressMode, setProgressMode] = useState(false);
  const [progress, setProgress] = useState(0);
  const [progressDone, setProgressDone] = useState(false);
  const [progressFailed, setProgressFailed] = useState(false);
  const rafRef = useRef<number>(0);
  const startTimeRef = useRef<number>(0);
  const dismissTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const clearTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const activeProgressRef = useRef(false);

  // Animate fake progress: fast start, slows toward 90%
  const animateProgress = useCallback(function animateProgress() {
    const elapsed = (Date.now() - startTimeRef.current) / 1000;
    // Asymptotic curve: approaches 90% over ~15s
    const pct = 90 * (1 - Math.exp(-elapsed / 5));
    setProgress(pct);
    rafRef.current = requestAnimationFrame(animateProgress);
  }, []);

  const showFlash = useCallback((msg: string) => {
    // Don't override an active progress toast with a flash
    if (activeProgressRef.current) return;

    setProgressMode(false);
    setProgressDone(false);
    setProgressFailed(false);
    setMessage(msg);
    setVisible(true);
    clearTimeout(dismissTimerRef.current);
    clearTimeout(clearTimerRef.current);
    dismissTimerRef.current = setTimeout(() => setVisible(false), 2500);
    clearTimerRef.current = setTimeout(() => {
      setMessage(prev => prev === msg ? null : prev);
    }, 2900);
  }, []);

  const showProgress = useCallback((msg: string) => {
    activeProgressRef.current = true;
    clearTimeout(dismissTimerRef.current);
    clearTimeout(clearTimerRef.current);
    cancelAnimationFrame(rafRef.current);
    setProgress(0);
    setProgressDone(false);
    setProgressFailed(false);
    setProgressMode(true);
    setMessage(msg);
    setVisible(true);
    startTimeRef.current = Date.now();
    rafRef.current = requestAnimationFrame(animateProgress);
  }, [animateProgress]);

  const finishProgress = useCallback((msg: string, success: boolean) => {
    activeProgressRef.current = false;
    cancelAnimationFrame(rafRef.current);
    setProgress(100);
    setProgressDone(true);
    setProgressFailed(!success);
    setMessage(msg);

    // Hold at 100% briefly, then dismiss
    clearTimeout(dismissTimerRef.current);
    clearTimeout(clearTimerRef.current);
    dismissTimerRef.current = setTimeout(() => setVisible(false), success ? 2000 : 3000);
    clearTimerRef.current = setTimeout(() => {
      setMessage(null);
      setProgressMode(false);
      setProgressDone(false);
      setProgress(0);
    }, success ? 2400 : 3400);
  }, []);

  useEffect(() => {
    showToastFn = showFlash;
    startProgressFn = showProgress;
    endProgressFn = finishProgress;
    return () => {
      showToastFn = null;
      startProgressFn = null;
      endProgressFn = null;
      cancelAnimationFrame(rafRef.current);
      clearTimeout(dismissTimerRef.current);
      clearTimeout(clearTimerRef.current);
    };
  }, [showFlash, showProgress, finishProgress]);

  if (!message) return null;

  // Determine if this is a completion message (contains "ready" or starts with checkmark)
  const isCompletion = !progressMode && (message.includes('ready') || message.startsWith('✓'));
  const isProgressComplete = progressMode && progressDone;
  const isFailure = progressMode && progressDone && progressFailed;
  const isSuccess = isCompletion || (isProgressComplete && !progressFailed);

  return (
    <div
      role={isFailure ? 'alert' : 'status'}
      className={`fixed bottom-6 left-1/2 -translate-x-1/2 z-50 transition-all duration-300 ${
        visible
          ? 'opacity-100 translate-y-0'
          : 'opacity-0 translate-y-4'
      }`}
    >
      <div
        className={`relative overflow-hidden flex items-center gap-2.5 px-5 py-3 shadow-lg text-sm font-medium ${
          progressMode ? 'rounded-2xl min-w-[280px]' : 'rounded-full'
        } ${
          isFailure ? 'bg-card text-danger border border-danger/30' : isSuccess
            ? 'bg-primary text-white shadow-[0_4px_20px_rgba(13,124,102,0.3)]'
            : 'bg-white text-foreground border border-border shadow-[0_4px_20px_rgba(0,0,0,0.08)]'
        }`}
      >
        {/* Icon */}
        <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${
          isSuccess ? 'bg-white/20' : 'bg-primary/10'
        }`}>
          {isFailure ? <span aria-hidden="true" className="text-danger">×</span> : progressMode && !progressDone ? (
            <svg className="w-3 h-3 text-primary animate-spin" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
            </svg>
          ) : (
            <svg className={`w-3 h-3 ${isSuccess ? 'text-white' : 'text-primary'}`} fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
          )}
        </div>

        {/* Text */}
        <span className="flex-1">{message}</span>

        {/* Progress bar */}
        {progressMode && (
          <div className={`absolute bottom-0 left-0 right-0 h-1 ${isSuccess ? 'bg-white/20' : 'bg-primary/10'}`}>
            <div
              className={`h-full transition-all ${
                progressDone ? 'duration-300' : 'duration-100'
              } ${isSuccess ? 'bg-white/60' : 'bg-primary'}`}
              style={{ width: `${progress}%` }}
            />
          </div>
        )}
      </div>
    </div>
  );
};
